import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import { useKindeAuth } from '@kinde-oss/kinde-auth-react'
import { CircularProgress, Dialog, DialogContent } from '@mui/material'
import { useSnackbar } from 'notistack'
import PropTypes from 'prop-types'

import { useAddOrderItemsFromPDFMutation } from '@/app/services/orderItems'
import { useCreateOrderMutation } from '@/app/services/orders'
import { selectContactId } from '@/app/slices/contactsSlice'
import { selectCustomerId } from '@/app/slices/customerSlice'
import { selectGuestUser, selectIsGuestLoggedIn } from '@/app/slices/guestUserSlice'
import { selectOrganisationId } from '@/app/slices/organisationSlice'
import { useMutationErrorSnackbar } from '@/common/hooks/useMutationErrorSnackbar'
import { ORDER_USER_TYPE } from '@/common/utils'
import Pdf2Quote from '@/features/pdf-2-quote/Pdf2Quote'

const classes = {
    dialogContent: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        p: 0,
    },
}

const AddPDFPartsDialog = ({ onClose, open }) => {
    const navigate = useNavigate()
    const { t } = useTranslation()
    const { enqueueSnackbar } = useSnackbar()
    const { showMutationError } = useMutationErrorSnackbar()
    const { isAuthenticated } = useKindeAuth()
    const { orderId: orderIdFromPath } = useParams()

    const organisationId = useSelector((state) => selectOrganisationId(state))
    const currentCustomerId = useSelector(selectCustomerId)
    const currentContactId = useSelector(selectContactId)
    const isGuestLoggedIn = useSelector(selectIsGuestLoggedIn)
    const guestUser = useSelector(selectGuestUser)

    const [createOrder, { reset: resetCreateOrder }] = useCreateOrderMutation()
    const [addOrderItemsFromPdf, { reset: resetUploadOrderItems }] = useAddOrderItemsFromPDFMutation()

    const [isInitializing, setIsInitializing] = useState(false)
    const [orderId, setOrderId] = useState(orderIdFromPath)

    const initializeOrder = async () => {
        if (!orderId && !isInitializing) {
            setIsInitializing(true)
            const userType = isAuthenticated ? ORDER_USER_TYPE.Customer : ORDER_USER_TYPE.Guest
            const contactId = isAuthenticated ? currentContactId : isGuestLoggedIn ? guestUser.contactId : null
            const customerId = isAuthenticated ? currentCustomerId : isGuestLoggedIn ? guestUser.customerId : null

            try {
                const createOrderResponse = await createOrder({
                    organisationId,
                    payload: {
                        organisationId,
                        quoteUserType: userType,
                        customerContactId: contactId,
                        customerId: customerId,
                    },
                }).unwrap()
                setOrderId(createOrderResponse.id)
            } catch (error) {
                showMutationError(error, t('Failed to create order'))
                handleClose()
            } finally {
                setIsInitializing(false)
            }
        }
    }

    const handleClose = () => {
        onClose()
    }

    const handleAddPDFParts = async () => {
        try {
            await addOrderItemsFromPdf({ organisationId, orderId }).unwrap()
            enqueueSnackbar(`${t('PDF part added to order')}`, { variant: 'success' })
        } catch (error) {
            console.error(error)
            showMutationError(error, t('$t(An error occurred) adding PDF part'))
        } finally {
            handleClose()
            resetUploadOrderItems()
            resetCreateOrder()
        }

        if (!orderIdFromPath) {
            navigate(`orders/${orderId}`)
        }
    }

    if (open && !orderId && !isInitializing) {
        initializeOrder()
    }

    return (
        <Dialog
            fullScreen={true}
            open={open}
            sx={classes.dialogRoot}
            onClose={handleClose}
        >
            {orderId && !isInitializing ? (
                <DialogContent sx={classes.dialogContent}>
                    <Pdf2Quote
                        orderId={orderId}
                        onCancelClicked={handleClose}
                        onSubmitClicked={handleAddPDFParts}
                    />
                </DialogContent>
            ) : (
                <DialogContent sx={classes.dialogContent}>
                    <CircularProgress />
                </DialogContent>
            )}
        </Dialog>
    )
}

AddPDFPartsDialog.propTypes = {
    open: PropTypes.bool,
    onClose: PropTypes.func,
}

export default AddPDFPartsDialog
